/**
 * Store Error Handler Hook
 * 
 * Listens for store error/warning/success events and displays
 * notifications to the user. No more silent failures!
 */

import { useEffect, useRef } from 'react';
import { useUIStore } from '../../stores/prioritization/uiStore';

interface StoreEventDetail {
  store?: string;
  action?: string;
  message: string;
  severity?: 'error' | 'warning' | 'info';
  /** Stage 7: stores often dispatch { message, type } only */
  type?: 'error' | 'warning';
}

/**
 * Identical store events that fire in a burst (e.g. a bulk import where every
 * row hits the same sync failure) are coalesced into ONE toast with a count
 * instead of a stack of duplicates. The flush is debounced per key, so as long
 * as the same event keeps arriving within this window it keeps accumulating.
 */
const COALESCE_WINDOW_MS = 900;

export function useStoreErrorHandler() {
  const addNotification = useUIStore((state) => state.addNotification);

  // key → accumulated notification awaiting flush. Kept in a ref so it survives
  // re-renders without re-subscribing the listeners.
  const pending = useRef<
    Map<string, { type: 'error' | 'warning'; title: string; message: string; count: number; timer: ReturnType<typeof setTimeout> }>
  >(new Map());

  useEffect(() => {
    const map = pending.current;

    const flush = (key: string) => {
      const p = map.get(key);
      if (!p) return;
      map.delete(key);
      addNotification({
        type: p.type,
        title: p.title,
        message: p.count > 1 ? `${p.message} (×${p.count})` : p.message,
        duration: p.type === 'error' ? 8000 : 6000,
      });
    };

    // Coalesce by (type, title, message): a burst of the same event becomes one
    // toast whose count reflects how many fired.
    const enqueue = (type: 'error' | 'warning', title: string, message: string) => {
      const key = `${type}|${title}|${message}`;
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        clearTimeout(existing.timer);
        existing.timer = setTimeout(() => flush(key), COALESCE_WINDOW_MS);
      } else {
        map.set(key, { type, title, message, count: 1, timer: setTimeout(() => flush(key), COALESCE_WINDOW_MS) });
      }
    };

    const handleError = (event: CustomEvent<StoreEventDetail>) => {
      const d = event.detail ?? {};
      const message = d.message ?? 'Operation failed';
      const store = d.store ?? 'Store';
      const action = d.action ?? 'action';
      console.error(`[${store}] ${action} failed:`, message);
      enqueue('error', store ? `${store} Error` : 'Error', message);
    };

    const handleWarning = (event: CustomEvent<StoreEventDetail>) => {
      const d = event.detail ?? {};
      const message = d.message ?? 'Something went wrong.';
      const store = d.store;
      const action = d.action;
      const notifType = d.type === 'error' ? 'error' : 'warning';
      console.warn(store && action ? `[${store}] ${action} warning:` : '[store-warning]', message);
      const title = store && action ? `${store} — ${action}` : notifType === 'error' ? 'Operation failed' : 'Sync Warning';
      enqueue(notifType, title, message);
    };

    const handleSuccess = (event: CustomEvent<StoreEventDetail>) => {
      const d = event.detail ?? {};
      const { store, action, message } = d;
      console.log(store && action ? `[${store}] ${action}:` : '[store-success]', message ?? '');
    };

    window.addEventListener('store-error', handleError as EventListener);
    window.addEventListener('store-warning', handleWarning as EventListener);
    window.addEventListener('store-success', handleSuccess as EventListener);

    return () => {
      for (const p of map.values()) clearTimeout(p.timer);
      map.clear();
      window.removeEventListener('store-error', handleError as EventListener);
      window.removeEventListener('store-warning', handleWarning as EventListener);
      window.removeEventListener('store-success', handleSuccess as EventListener);
    };
  }, [addNotification]);
}

/**
 * Helper to dispatch store errors from anywhere
 */
export function dispatchStoreError(store: string, action: string, message: string) {
  window.dispatchEvent(new CustomEvent('store-error', { 
    detail: { store, action, message, severity: 'error' } 
  }));
}

/**
 * Helper to dispatch store warnings from anywhere
 */
export function dispatchStoreWarning(store: string, action: string, message: string) {
  window.dispatchEvent(new CustomEvent('store-warning', { 
    detail: { store, action, message, severity: 'warning' } 
  }));
}

/**
 * Helper to dispatch store success from anywhere
 */
export function dispatchStoreSuccess(store: string, action: string, message: string) {
  window.dispatchEvent(new CustomEvent('store-success', { 
    detail: { store, action, message } 
  }));
}
